import React from "react";

import { faEllipsisH } from "@performyard/fontawesome/pro-regular-svg-icons";
import {
	AvatarProfile,
	Chip,
	DropDownMenu,
	Icon,
	Link,
	ModalConfirmation,
	Text,
	TextDate,
	toast,
} from "@performyard/py-components";
import type { ActionItemWithMeetingInfoJson } from "@performyard/service-contracts";

import ScrollableTable from "../../../../components/TableLite/TableLiteScrollable";
import { LinkifiedText } from "../../MeetingDetails/components/LinkifiedText";
import EditActionItemModal from "./EditActionItemModal";

import { useEditMeetingActionItem } from "../../../../api/hooks";

import EmptySearchTableMessage from "../../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";

interface ActionItemsTableProps {
	actionItems: ActionItemWithMeetingInfoJson[];
	isFetchingActionItems: boolean;
	activeSort: string;
	activeSortDirection: string;
	handleColumnHeaderClick: (column: { name: string; direction: "asc" | "desc" }) => void;
	userId: string;
}

const ActionItemsTable = (props: ActionItemsTableProps) => {
	const { actionItems, isFetchingActionItems, activeSort, activeSortDirection, handleColumnHeaderClick, userId } =
		props;

	const { mutate: editActionItem, isPending: isUpdating } = useEditMeetingActionItem();

	const [editingActionItem, setEditingActionItem] = React.useState<ActionItemWithMeetingInfoJson | null>(null);
	const [deletingActionItemId, setDeletingActionItemId] = React.useState<string | null>(null);

	const getAssignee = (actionItem: ActionItemWithMeetingInfoJson) => {
		return actionItem.participants.find((p) => p.role === "assignee");
	};

	const isCreator = (actionItem: ActionItemWithMeetingInfoJson) => {
		return actionItem.created.userId === userId;
	};

	const handleStatusToggle = (actionItem: ActionItemWithMeetingInfoJson) => {
		const newStatus: "complete" | "incomplete" =
			actionItem.mostRecentMeetingStatus === "complete" ? "incomplete" : "complete";
		const updatedMeetings = actionItem.meetings.map((m) =>
			m.meetingId === actionItem.mostRecentMeetingId ? { meetingId: m.meetingId, status: newStatus } : m
		);

		editActionItem(
			{
				actionItemId: actionItem._id,
				data: {
					meetings: updatedMeetings,
				},
			},
			{
				onSuccess: () => {
					const message =
						newStatus === "complete" ? "Action Item Marked Complete" : "Action Item Marked Incomplete";
					toast.success(message);
				},
				onError: () => {
					toast.error("Failed to update action item");
				},
			}
		);
	};

	const handleDelete = (actionItemId: string) => {
		const actionItem = actionItems.find((item) => item._id === actionItemId);
		if (!actionItem) return;

		const updatedMeetings = actionItem.meetings.map((m) =>
			m.meetingId === actionItem.mostRecentMeetingId ? { ...m, status: "deleted" as const } : m
		);

		editActionItem(
			{
				actionItemId,
				data: {
					meetings: updatedMeetings,
				},
			},
			{
				onSuccess: () => {
					setDeletingActionItemId(null);
					toast.success("Action Item Updated");
				},
				onError: () => {
					toast.error("Failed to delete action item");
				},
			}
		);
	};

	const columns = [
		{
			id: "name",
			label: "Action Item Name",
			isSortable: true,
			width: 308,
			getter: (actionItem: ActionItemWithMeetingInfoJson) => {
				return (
					<Text size="paragraph-3" weight="regular" dangerouslySetClassName="py-line-clamp-1 max-w-[308px]">
						<LinkifiedText text={actionItem.name} />
					</Text>
				);
			},
		},
		{
			id: "assignee",
			label: "Assignee",
			isSortable: true,
			width: 148,
			getter: (actionItem: ActionItemWithMeetingInfoJson) => {
				const assignee = getAssignee(actionItem);
				if (assignee) {
					return <AvatarProfile employeeId={assignee.userId} employeeName={assignee.name || ""} />;
				}
				return <AvatarProfile employeeId="" employeeName="Unassigned" noLink />;
			},
		},
		{
			id: "status",
			label: "Status",
			isSortable: true,
			width: 80,
			getter: (actionItem: ActionItemWithMeetingInfoJson) => {
				if (actionItem.mostRecentMeetingStatus === "complete") {
					return <Chip label="Complete" color="green" size={24} />;
				}
				return <Chip label="Incomplete" color="neutral" size={24} />;
			},
		},
		{
			id: "dueDate",
			label: "Due Date",
			isSortable: true,
			width: 80,
			getter: (actionItem: ActionItemWithMeetingInfoJson) => {
				if (actionItem.dueDate) {
					return <TextDate date={actionItem.dueDate} hasTime={false} dateSize="medium" />;
				}
			},
		},
		{
			id: "createdBy",
			label: "Created By",
			isSortable: true,
			width: 144,
			getter: (actionItem: ActionItemWithMeetingInfoJson) => {
				if (actionItem.created.userId && actionItem.created.name) {
					return (
						<AvatarProfile employeeId={actionItem.created.userId} employeeName={actionItem.created.name} />
					);
				}
				return <AvatarProfile employeeId="" employeeName="Unknown" noLink />;
			},
		},
		{
			id: "workspaceName",
			label: "Meeting Workspace Name",
			isSortable: true,
			width: 220,
			getter: (actionItem: ActionItemWithMeetingInfoJson) => {
				return (
					<Link href={`/meeting-workspaces/${actionItem.workspaceId}`}>
						<Text
							size="paragraph-3"
							weight="regular"
							dangerouslySetClassName="py-line-clamp-1 max-w-[210px]"
						>
							{actionItem.workspaceName}
						</Text>
					</Link>
				);
			},
		},
		{
			id: "control",
			label: "",
			isSortable: false,
			width: 36,
			getter: (actionItem: ActionItemWithMeetingInfoJson) => {
				const creator = isCreator(actionItem);
				const assignee = getAssignee(actionItem);
				const isAssignee = assignee?.userId === userId;

				// Build options based on permissions
				const options: Array<{ key: string; label: string; onClick: () => void }> = [];

				// Everyone who can see the item can mark complete/incomplete
				if (creator || isAssignee) {
					const statusLabel =
						actionItem.mostRecentMeetingStatus === "complete" ? "Mark Incomplete" : "Mark Complete";
					options.push({
						key: "toggle-status",
						label: statusLabel,
						onClick: () => handleStatusToggle(actionItem),
					});
				}

				// Only creator can edit and delete
				if (creator) {
					options.push({
						key: "edit",
						label: "Edit",
						onClick: () => setEditingActionItem(actionItem),
					});
					options.push({
						key: "delete",
						label: "Delete",
						onClick: () => setDeletingActionItemId(actionItem._id),
					});
				}

				if (options.length === 0) {
					return <div className="h-8" />;
				}

				return (
					<div className="flex items-center justify-end">
						<DropDownMenu options={options} placement="bottom-end" className="group flex items-center">
							<Icon
								icon={faEllipsisH}
								size={24}
								color="neutral-7"
								dangerouslySetClassName="transition-colors group-hover:text-neutral-9"
							/>
						</DropDownMenu>
					</div>
				);
			},
		},
	];

	return (
		<>
			<div data-testid="actionItemsContainer">
				<ScrollableTable
					rowKey="_id"
					columns={columns}
					isLoading={isFetchingActionItems || isUpdating}
					rows={actionItems}
					activeSort={activeSort}
					activeSortDirection={activeSortDirection}
					onColumnClick={(columnID, nextDirection) =>
						handleColumnHeaderClick({ name: columnID, direction: nextDirection as "asc" | "desc" })
					}
					emptyChildren={<EmptySearchTableMessage mainText="No Action Items Found" />}
				/>
			</div>

			<EditActionItemModal
				isOpen={editingActionItem !== null}
				handleClose={() => setEditingActionItem(null)}
				actionItem={editingActionItem}
			/>

			<ModalConfirmation
				isOpen={deletingActionItemId !== null}
				onClose={() => setDeletingActionItemId(null)}
				title="Delete Action Item?"
				subtitle="Are you sure you want to delete this action item? This action is permanent and cannot be undone."
				negativeButtonProps={{
					children: "Cancel",
					onClick: () => setDeletingActionItemId(null),
				}}
				positiveButtonProps={{
					children: "Yes, Delete Action Item",
					onClick: () => {
						if (deletingActionItemId) {
							handleDelete(deletingActionItemId);
						}
					},
					disabled: isUpdating,
					color: "red",
				}}
				dangerouslySetClassName="max-w-[416px]"
			/>
		</>
	);
};

export default ActionItemsTable;
